import { BasicSymbolType } from "../../../dev/reel/symbol/config/IReelSymbolStruct";
import { GameConfig } from "../../config/base/GameConfig";
import { IWinRatioConfig } from "../../config/base/IWinConfig";
import { IAllWinData, IBetWayWinData, CarFsInfo, stickyFS } from './IData';
import { Helper } from "../../../dev/utils/Helper";
import { ArrayHelper } from "../../../dev/utils/ArrayHelper";
import { CoreDataBuilder } from "./CoreDataBuilder";
import { GameMode } from '@nolimitcity/slot-game/bin/flowcontrol/GameMode';
import { SymbolData } from "../../../dev/reel/symbol/config/SymbolData";
import { RoadRage } from "../../RoadRage";

export enum CarTypes {
    RED = "WC1",
    GREEN = "WC2",
    YELLOW = "WC3"
}

export interface CarMoveInfo {
    reel: number;
    row: number;
    reelTo: number;
    rowTo: number;
    collides: boolean;
    type: string;
    value?: number;
    toCollect?: boolean;
    sticky?: boolean;
}

export class ReelGameData extends CoreDataBuilder {

    protected _reelsNextSpin: string;
    protected _currentWindowReels: string[][];

    protected _carInfo: CarMoveInfo[];

    protected _redCarPositions: number[][];
    protected _greenCarPositions: number[][];
    protected _yellowCarPositions: number[][];

    protected _savedStickies: stickyFS[] = [];

    private filterReelToWindowReels(size: number[]): string[][] {
        return this.reels.map((r, reel) => r.map((w, row) => {
            if (row == 0 || size[reel] < row) return "";
            return w;
        }).filter(v => !!v));
    }

    public saveLastStickes(reset:boolean): void {
        this._savedStickies = reset ? [] : this.stickWildPos;
    }

    public get windowReel(): string[][] {
        return this._currentWindowReels;
    }

    public get reels(): string[][] {
        return this.data.reels;
    }

    public get setupMode(): boolean {
        return Helper.fetchOrApply(this.data.mode === "FREESPIN_SETUP", false);
    }

    public get reelsNextSpin(): string {
        return this._reelsNextSpin = Helper.fetchOrApply(this.data.nextMode, this.data.mode);
    }

    public get reelsWithScatters(): boolean[] {
        return Helper.fetchOrApply(this.data.scatterReels, ArrayHelper.initArrayWithValues(this.reels.length, () => false));
    }

    public get worldSize(): number {
        if(!this.data.fsInfo.fsCars) return 3;
        else{
           return 2 + Object.keys(this.data.fsInfo.fsCars).length;
        }
    }

    public get amountOfScattersLanded(): number {
        return ArrayHelper.countWithCondition(this.reelsWithScatters,(v) => v);
    }

    public get scatterHaveLanded(): boolean {
        return this.reelsWithScatters.includes(true);
    }

    public get scatterPositions(): number[] {
        return this.fetchSymbolPos("S", this.windowReel, true) as number[];
    }

    public get mysteryPos(): number[] {
        return this.fetchSymbolPos("M", this.windowReel, true) as number[];
    }

    public get gotMystery(): boolean {
        return !!this.mysteryPos.filter(v => v >= 0).length;
    }

    public get MysteryRevealValue(): string[] {
        const arr:string[] = ArrayHelper.initArrayWithValues(this.mysteryPos.length,(l) => this.evaluateArea[l][this.mysteryPos[l]]);
        
        let missingVal = "MWC1";

        Object.keys(this.fsInfo.fsCars).forEach((v,i) => { if(v === missingVal) missingVal = "MWC"+(i+2) })

        for(let i = 0; i < arr.length;i++){
            if(arr[i] === "MSPIN"){
                arr[i] = missingVal;
            }
        }
        return arr;
    }

    public get CarsFSAvailable():string[]{
        return Object.keys(this.fsInfo.fsCars);
    }

    public get constructionValues(): string[][] {
        const constrPos: number[][] = this.fetchSymbolPos("F4", this.windowReel, false) as number[][];
        const temp: string[][] = ArrayHelper.initArrayWithValues(constrPos.length, () => []);

        for (let reel: number = 0; reel < constrPos.length; reel++) {
            for (let row: number = 0; row < constrPos[reel].length; row++) {
                temp[reel].push(this.evaluateArea[reel][constrPos[reel][row]])
            }
        }

        return temp;
    }

    public get constructionPositions(): number[][] {
        return this.fetchSymbolPos("F4", this.windowReel, false) as number[][];
    }

    public get WildPositions(): number[][] {
        return this.fetchSymbolPos(BasicSymbolType.WILD, this.evaluateArea, false) as number[][];
    }

    public get RedAltCarPositions(): number[][] {
        return this.fetchSymbolPos(CarTypes.RED, this.evaluateArea, false) as number[][];
    }
    public get GreenAltCarPositions(): number[][] {
        return this.fetchSymbolPos(CarTypes.GREEN, this.evaluateArea, false) as number[][];
    }
    public get YellowAltCarPositions(): number[][] {
        return this.fetchSymbolPos(CarTypes.YELLOW, this.evaluateArea, false) as number[][];
    }

    public get RedCarPositions(): number[][] {
        return this._redCarPositions;
    }

    public get GreenCarPositions(): number[][] {
        return this._greenCarPositions;
    }

    public get YellowCarPositions(): number[][] {
        return this._yellowCarPositions;
    }

    public get amountOfCars(): number {
        let amm: number = 0;
        this.RedCarPositions.forEach(r => amm += ArrayHelper.countWithCondition(r, (v) => v >= 0))
        this.GreenCarPositions.forEach(r => amm += ArrayHelper.countWithCondition(r, (v) => v >= 0))
        this.YellowCarPositions.forEach(r => amm += ArrayHelper.countWithCondition(r, (v) => v >= 0))
        return amm;
    }

    public get amountOfPlusSpin(): number {
        return this.countTotalAmountOfType("MSPIN", this.evaluateArea);
    }

    public get amountOfScatters(): number {
        return this.countTotalAmountOfType("S", this.windowReel);
    }

    public get symbolSizeMap(): number[][] {
        return Helper.fetchOrApply(this.symbolSizes, ArrayHelper.countAmountOfIndexes(this.windowReel));
    }

    public get totalPossibleWays(): number {
        return Helper.fetchOrApply(this.data.totalWays, 243);
    }

    public get symbolSizes(): number[][] {
        return Helper.fetchOrApply(this.data.symbolSizes, [[]]);
    }

    public get amountOfWaysOfSymbolSize(): number {
        let amountOfWays: number = 0;
        this.symbolSizes.forEach((v) => {
            amountOfWays = amountOfWays === 0 ? ArrayHelper.arraySum(v) : amountOfWays * ArrayHelper.arraySum(v);
        });
        return amountOfWays
    }

    public get wildBoolMap(): boolean[][] {
        return this.symbolBooleanPos(BasicSymbolType.WILD, this.evaluateArea);
    }

    public get reelModeHeight(): number {
        if(this.data.fsInfo.fsCars){
            if(Object.keys(this.data.fsInfo.fsCars).length > 1)
                return Object.keys(this.data.fsInfo.fsCars).length + 2;
            else
                return 3;
        }else{
            return Math.max(this.symbolSizes[0].length,3);
        }
    }

    public get gameAreaSize(): number[] {
        return Helper.fetchOrApply(ArrayHelper.initArrayWithValues(this.data.reels.length, (i) => this.reelModeHeight), []);
    }

    public get carMoveInfo(): CarMoveInfo[] {
        return this._carInfo;
    }

    public get evaluateArea(): string[][] {
        return Helper.fetchOrApply(this.data.evaluatedArea, []);
    }

    public get altEvaluateArea(): string[][] {
        return Helper.fetchOrApply(this.data.alternativeEvaluatedReels, []);
    }

    public get fsInfo(): CarFsInfo {
        return Helper.fetchOrApply(this.data.fsInfo, {});
    }

    public get stickWildPos(): stickyFS[] {
        return Helper.fetchOrApply(this.fsInfo.stickyWilds, []);
    }

    public get lastStickies(): stickyFS[] {
        return this._savedStickies;
    }

    private genCarMoveInfo(): CarMoveInfo[] {
        const temp: CarMoveInfo[][] = [];
        for (let row: number = 0; row < this.reelModeHeight; row++) {
            let carTypeIndxes: number = findAnyCarOnRow(row, this.RedCarPositions, this.GreenCarPositions, this.YellowCarPositions);
            temp.push([]);
            if (carTypeIndxes) {

                const scatters: number[] = scatterOnRow(this.scatterPositions, row).filter(v => v >= 0);
                const mysteries: number[] = scatterOnRow(this.mysteryPos, row).filter(v => v >= 0);
                // const wilds: number[] = scatterOnRow(filterWildForRow(this.WildPositions, row), row).filter(v => v >= 0);

                const redCars: CarMoveInfo[] = includesRed(carTypeIndxes) ? genBasicInfo(this.RedCarPositions, row, CarTypes.RED) : [];
                const greenCars: CarMoveInfo[] = includesGreen(carTypeIndxes) ? genBasicInfo(this.GreenCarPositions, row, CarTypes.GREEN) : [];
                const yellowCars: CarMoveInfo[] = includesYellow(carTypeIndxes) ? genBasicInfo(this.YellowCarPositions, row, CarTypes.YELLOW) : [];

                redCars.forEach((c, i) => {
                    c.sticky = !!this._savedStickies.find((v => v.reel === c.reel && v.row == c.row));
                })

                greenCars.forEach((c, i) => {
                    c.sticky = !!this._savedStickies.find((v => v.reel === c.reel && v.row == c.row));
                })

                // Red Logic - Scatters & Yellow -> Red
                if (redCars.length) {
                    redCars.forEach((c, i) => {
                        if(c.sticky) return;

                        // let wild: number = fetchVal(ArrayHelper.fetchLastValue(wilds.filter(y => y < c.reel)));
                        let scatter: number = fetchVal(ArrayHelper.fetchLastValue(scatters.filter(s => s < c.reel)));
                        let myst: number = fetchVal(ArrayHelper.fetchLastValue(mysteries.filter(m => m < c.reel)));

                        let green: number = greenCars.length ? fetchReelFromCarObj(ArrayHelper.fetchLastValue(greenCars.filter(y => y.reel < c.reel))) : -1;
                        let yellow: number = yellowCars.length ? fetchReelFromCarObj(ArrayHelper.fetchLastValue(yellowCars.filter(y => y.reel < c.reel))) : -1;

                        const prevRed:number = redCars[i-1] ? redCars[i-1].reelTo  : -1;

                        const filteredNonValues: number[] = [prevRed, scatter, myst, green, yellow].filter(v => v != -1);
                        const closest: number = filteredNonValues.length ? Math.max(...filteredNonValues) : -1;

                        if (i == 0) { // first Car
                            if (closest != -1) {

                                c.reelTo = closest + 1;

                                if (closest == green) {
                                    if(this._savedStickies.find((v => v.reel === green && v.row == c.row))){
                                        c.collides = false;
                                        c.reelTo = green+1;
                                    }else{
                                        c.collides = !((c.reel - green) % 2) // Uneven will do an collision.
                                        c.reelTo = c.collides ? c.reel - 1 : c.reel; // An collison only happens if it moves.
                                        c.reelTo -= (c.reel - green) > 2 ? 1 : 0; // If there is two or more steps. Move one more.
                                    }
                                }
                            } else {
                                c.reelTo = i;
                            }
                        } else { // second & third car
                            if (closest != -1) {
                                c.reelTo = closest + 1;
                            } else {
                                c.reelTo = redCars[i - 1].reelTo + 1;
                            }


                            if (closest == green) {
                                if(this._savedStickies.find((v => v.reel === green && v.row == c.row))){
                                    c.collides = false;
                                    c.reelTo = green+1;
                                }else{
                                    c.collides = !((c.reel - green) % 2) // Uneven will do an collision.
                                    c.reelTo = c.collides ? c.reel - 1 : c.reel; // An collison only happens if it moves.
                                    c.reelTo -= (c.reel - green) > 2 ? 1 : 0; // If there is two or more steps. Move one more.
                                }
                                return;
                            }

                            for (let k: number = 0; k < i; k++) {
                                if (c.reelTo === redCars[k].reelTo)
                                    c.reelTo = redCars[k].reelTo + 1;
                            }
                        }
                    })
                }
                if (greenCars.length) {
                    greenCars.reverse();
                    greenCars.forEach((c, i) => {
                        if(c.sticky) return;
                        // let wild: number = fetchVal(ArrayHelper.fetchLastValue(wilds.filter(y => y < c.reel)));
                        let scatter: number = fetchVal(ArrayHelper.fetchFirstValue(scatters.filter((s) => s > c.reel)));
                        let myst: number = fetchVal(ArrayHelper.fetchLastValue(mysteries.filter(m => m > c.reel)));

                        let red: number = redCars.length ? fetchReelFromCarObj(ArrayHelper.fetchFirstValue(redCars.filter((y) => y.reel > c.reel))) : -1;
                        let yellow: number = yellowCars.length ? fetchReelFromCarObj(ArrayHelper.fetchFirstValue(yellowCars.filter((y) => y.reel > c.reel))) : -1;

                        const prevGreen:number = greenCars[i-1] ? greenCars[i-1].reelTo  : -1;
                        const filteredNonValues: number[] = [prevGreen, scatter, myst, red, yellow].filter(v => v != -1);
                        const closest: number = filteredNonValues.length ? Math.min(...filteredNonValues) : -1;
                        
                        if (i == 0) { // first Car
                            if (closest != -1) {
                                c.reelTo = closest - 1;

                                if (closest == red) {
                                    if(this._savedStickies.find((v => v.reel === red && v.row == c.row))){
                                        c.collides = false;
                                        c.reelTo = red-1;
                                    }else{
                                        c.collides = !((red - c.reel) % 2) // Uneven will do an collision.
                                        c.reelTo = c.collides ? c.reel + 1 : c.reel; // An collison only happens if it moves.
                                        c.reelTo += (red - c.reel) > 2 ? 1 : 0; // If there is two or more steps. Move one more.
                                    }
                                }
                            } else
                                c.reelTo = this.data.reels.length - 1;

                        } else { // second & third car

                            if (closest != -1) {
                                c.reelTo = closest - 1;
                            } else {
                                c.reelTo = greenCars[i - 1].reelTo - 1;
                            }

                            if (closest == red) {
                                if(this._savedStickies.find((v => v.reel === red && v.row == c.row))){
                                    c.collides = false;
                                    c.reelTo = red-1;
                                }else{
                                    c.collides = !((red - c.reel) % 2) // Uneven will do an collision.
                                    c.reelTo = c.collides ? c.reel + 1 : c.reel; // An collison only happens if it moves.
                                    c.reelTo += (red - c.reel) > 2 ? 1 : 0; // If there is two or more steps. Move one more.
                                }
                                return;
                            }

                            for (let k: number = 0; k < i; k++) {
                                if (c.reelTo === greenCars[k].reelTo)
                                    c.reelTo = greenCars[k].reelTo - 1;
                            }
                        }
                    })
                }
                temp[row].push(...redCars, ...greenCars, ...yellowCars);
            }
        }

        //return [].concat.apply([], temp); :woozy:
        return temp.reduce((a, b) => a.concat(b), []).map((car) => Object.assign({}, car));

        function includesRed(val: number): boolean { return !!(val & 1); }
        function includesGreen(val: number): boolean { return !!(val & 2); }
        function includesYellow(val: number): boolean { return !!(val & 4); }

        function fetchVal(val: number): number {
            return val === undefined ? -1 : val;
        }

        function filterWildForRow(arr: number[][], row: number): number[] {
            const temp: number[] = [];
            for (let reel: number = 0; reel < arr.length; reel++) temp.push(arr[reel][row])
            return temp;
        }

        function findAnyCarOnRow(row: number, redCars: number[][], greenCars: number[][], yellowCars: number[][]): number {
            let typeVal: number = 0;
            typeVal += carOnRow(redCars, row) ? 1 : 0;
            typeVal += carOnRow(greenCars, row) ? 2 : 0;
            typeVal += carOnRow(yellowCars, row) ? 4 : 0;
            return typeVal;
        }
        function carOnRow(arr: number[][], row: number): boolean {
            for (let r: number = 0; r < arr.length; r++) if (arr[r].indexOf(row) >= 0) return true;
            return false;
        }

        function fetchReelFromCarObj(val: CarMoveInfo): number {
            return val ? val.reel : -1;
        }

        function scatterOnRow(arr: number[], row: number): number[] {
            return ArrayHelper.initArrayWithValues(arr.length, (i) => { return arr[i] === row ? i : -1 });
        }

        function genBasicInfo(arr: number[][], row: number, type: string): CarMoveInfo[] {
            const gen: CarMoveInfo[] = [];
            arr.forEach((v, reel) => {
                if (v.includes(row)) {
                    gen.push({
                        reel: reel,
                        row: row,
                        reelTo: reel,
                        rowTo: row,
                        collides: false,
                        type: type
                    })
                }
            })
            return gen;
        }
    }

    findCarOnRow(row: number, carType?: CarTypes): boolean {
        let carFound: boolean = false;
        this.carMoveInfo.forEach(c => {
            if (carFound) return;
            if (c.row === row) carFound = !!carType ? carType === c.type : true;
        });
        return carFound;
    }

    isCarOnReel(reel: number, carType?: CarTypes): boolean {
        return !!this.findCarOnReel(reel, carType);
    }

    findCarOnReel(reel: number, carType?: CarTypes): CarMoveInfo {
        return this.carMoveInfo.filter(c => c.reel == reel && (carType ? carType === c.type : true))[0];
    }

    apply(data: any): void {
        super.apply(data);
        // if(this.data.reels)
        // this.data.reels = [
        //     ["L2","WC2","L4","L4","L4",],
        //     ["L2","L4","L4","L4","L4",],
        //     ["L2","L4","L4","L4","L4",],
        //     ["L2","WC1","L4","L4","L4",],
        //     ["L2","WC2","L4","L4","L4",] ]
        // if(this.data.evaluatedArea)
        // this.data.evaluatedArea = [
        //     ["W","L4","L4"],
        //     ["WC2","L4","L4"],
        //     ["WC1","L4","L4"],
        //     ["W","L4","L4"],
        //     ["WC2","L4","L4",] ]

        if(this.data.mode === GameMode.NORMAL)
            this.saveLastStickes(true)
   
        this._currentWindowReels = this.filterReelToWindowReels(this.gameAreaSize);

        this._redCarPositions = this.fetchSymbolPos(CarTypes.RED, this.windowReel, false) as number[][];
        this._greenCarPositions = this.fetchSymbolPos(CarTypes.GREEN, this.windowReel, false) as number[][];
        this._yellowCarPositions = this.fetchSymbolPos(CarTypes.YELLOW, this.windowReel, false) as number[][];

        const fsStickyFilter:boolean = this.data.mode === GameMode.FREESPIN && !!this.lastStickies.length;

        if(fsStickyFilter){
            this.applyOldStickies(true);
        }
        
        this._carInfo = this.amountOfCars ? this.genCarMoveInfo() : [];
        
        if(fsStickyFilter){
            this.applyOldStickies(false);
            if (this.data.mode === GameMode.FREESPIN && this.lastStickies.length) {
                this._carInfo = this._carInfo.filter(cI => {
                    return this.lastStickies.findIndex(sI => sI.reel === cI.reel && sI.row === cI.row) === -1;
                })
            }
        }
        
    }

    public fetchCarTypePositions(type:string):number[][]{
        switch(type){
            case "WC1":
                return this._redCarPositions;
            case "WC2":
                return this._greenCarPositions;
            default:
                return this._yellowCarPositions;
        }
    }

    private applyOldStickies(enable:boolean){
        this.lastStickies.forEach(v => {
            const carPos:number[][] = this.fetchCarTypePositions(v.type);
            if(enable){
                if (carPos[v.reel].indexOf(v.row) == -1) carPos[v.reel].push(v.row);
            }else{
                ArrayHelper.removeFirstMatchElementWithCondition(carPos[v.reel],(r) => r === v.row);
            }
        })
    }
}

export class WinGameData extends CoreDataBuilder {

    private _winData: IBetWayWinData[] = [];
    private _normalWinData: IBetWayWinData[] = [];
    private _allBetWayWinData: IAllWinData;

    public CreateNearWinMap(lastStickies:stickyFS[]): boolean[][] {
        return this.createConnectedNearWinMap(this._allBetWayWinData,this.data.reels, this.data.evaluatedArea,lastStickies);
    }

    public get totalWinCombination(): number {
        return Helper.fetchOrApply(this.allBetWayWinData.totalWinCombination, 0);
    }

    public get brokeTheBank(): boolean {
        return Helper.fetchOrApply(this.data.brokeTheBank, false);
    }

    public get totalWin(): number {
        return Helper.fetchOrApply(this.data.accumulatedRoundWin, 0);
    }

    public get singleWin(): number {
        return Helper.fetchOrApply(this.data.totalSpinWinnings, 0)
    }

    public get playedBetValue(): number {
        return Helper.fetchOrApply(this.data.playedBetValue, 0)
    }

    public get betWayWins(): IBetWayWinData[] {
        return this._winData;
    }

    public get normalWinData(): IBetWayWinData[] {
        return this._normalWinData;
    }

    public get allBetWayWinData(): IAllWinData {
        return this._allBetWayWinData;
    }

    public get winRatio(): number {
        return this.singleWin / this.playedBetValue;
    }

    public get WereGoodFS(): boolean {
        return this.data.mode !== GameMode.NORMAL && this.totalWinRatio > (this.data.mode === GameMode.FREESPIN ? 100 : 400);
    }

    public get totalWinRatio(): number {
        return this.totalWin / this.playedBetValue;
    }

    public get isBigWin(): boolean {
        return this.winRatio > GameConfig.instance.WIN_RATIOS.bigWin[0].ratio * 1.2;
    }

    public get winRatioConfig(): IWinRatioConfig {
        return this.getWinRatioConfig(this.winRatio, GameConfig.instance.WIN_RATIOS.normalWin.concat().concat(GameConfig.instance.WIN_RATIOS.bigWin));
    }

    apply(data: any): void {
        super.apply(data);

        //#315 Filtering out wild line Wins position
        // if(this.data.evaluatedArea){
        //     this.data.evaluatedArea = [
        //         ["WC1","W","WC1","W","W"],
        //         ["WC1","WC1","WC1","W","W"],
        //         ["WC1","W","WC1","W","W"],
        //         ["W","W","W","W","W"],
        //         ["L2","L2","L1","L2","L5"]];
        // // Wins are linked to the length of the win. This prevent manipulations.
        //     this.data.betWayWins[0].length = 5;
        // }

        this._winData = this.data.evaluatedArea ? this.addBetLvls(this.createConnectedWinWays(this.data.betWayWins,this.data.evaluatedArea), this.playedBetValue, GameConfig.instance.WIN_RATIOS!.normalWin) : [];

        this.removeNonWildWinCombo(this._winData);

        this._allBetWayWinData = this.parseAllHits(this._winData, this.data.reels.length, Helper.fetchOrApply(this.data.evaluatedArea ? this.data.evaluatedArea[0].length : 3, 3));
        this._normalWinData =  this.combineWinData(this._winData);
    }

    //#315 Filtering out wild line Wins position
    removeNonWildWinCombo(winData:IBetWayWinData[]):void{

        if(!winData.length) return;

        for(let wI:number = 0; wI < winData.length; wI++){
            let wingrp = winData[wI];
            let ammWinSyms:number[] = [];
            let reelTrigged = false;
            ArrayHelper.eachForEach(wingrp.reelWinPositions,(reel,row,hits) => {
                if(!row) reelTrigged = false;

                if(reelTrigged) return;

                if(hits && SymbolData.isNormalSymbol(this.data.evaluatedArea[reel][row])){
                    ammWinSyms.push(reel);
                    reelTrigged = true;
                }
            })

            // If there are more than one symbols in two reel positions. the connection links & include the wilds connected to the symbol.
            if(ammWinSyms.length > 1) return;
            
            ammWinSyms.forEach(reel => {
                this.data.evaluatedArea[reel].forEach((sym,row) =>{
                    if(!SymbolData.isNormalSymbol(sym)){
                        winData[wI].reelWinPositions[reel][row] = false;
                        winData[wI].reelWinLvlPositions[reel][row] = -1;
                    }
                })
            })
        }
    
    }

    protected combineWinData(data:IBetWayWinData[]):IBetWayWinData[]{
        const temp:IBetWayWinData[] = [];

        if(RoadRage.apiPlugin.gameClientConfiguration.displayIndividualWinsInGame)
            return data;
        
        data.forEach(d => {
            let indxToFill:number = temp.findIndex(v => v.symbol == d.symbol);
            if(indxToFill == -1){
                indxToFill = temp.push({
                    amount: d.amount,
                    length: d.length,
                    multipliedAmount: d.multipliedAmount,
                    multiplier: d.multiplier,
                    numberOfCombinations: d.numberOfCombinations,
                    reelWinLvlPositions: 
                    ArrayHelper.initArrayWithValues(d.reelWinLvlPositions.length,(l) => 
                    ArrayHelper.initArrayWithValues(d.reelWinLvlPositions[l].length,(k) => d.reelWinLvlPositions[l][k])) ,
                    reelWinPositions:
                    ArrayHelper.initArrayWithValues(d.reelWinPositions.length,(l) => 
                    ArrayHelper.initArrayWithValues(d.reelWinPositions[l].length,(k) => d.reelWinPositions[l][k])) ,
                    symbol:d.symbol,
                    winLvl:d.winLvl
                });
                return;
            }
            
            temp[indxToFill].amount += d.amount;
            temp[indxToFill].length = d.length > temp[indxToFill].length ? d.length : temp[indxToFill].length;
            temp[indxToFill].multipliedAmount += d.multipliedAmount;
            temp[indxToFill].multiplier = d.multiplier;
            temp[indxToFill].numberOfCombinations += d.numberOfCombinations;
            ArrayHelper.eachForEach(temp[indxToFill].reelWinPositions,(reel,row) => {
                if(d.reelWinPositions[reel][row]) temp[indxToFill].reelWinPositions[reel][row] = true;
            })
        });
        temp.forEach(t => {
            let winLvl: number = 0;
            const amountRatio: number = t.amount / this.playedBetValue;
            GameConfig.instance.WIN_RATIOS!.normalWin.forEach((v, i) => v.ratio <= amountRatio ? winLvl = i : winLvl)!
            t.winLvl = winLvl;

            ArrayHelper.eachForEach(t.reelWinLvlPositions,(reel,row) => {
                if(t.reelWinPositions[reel][row]) t.reelWinLvlPositions[reel][row] = winLvl;
            })
        })

        return temp;
    }
}